import { useEffect, useRef } from 'react';
import { useNotifications, NOTIFICATION_TYPES } from '../contexts/NotificationContext';

/**
 * NotificationListener — کامپوننت بی‌رابط که به رویدادهای کل اپ گوش می‌ده
 * و در صورت لزوم اعلان درست می‌کنه.
 *
 * نحوه فراخوانی از هر جای پروژه:
 *
 *   window.dispatchEvent(new CustomEvent('notify', {
 *     detail: {
 *       type: 'order',
 *       title: 'سفارش جدید',
 *       message: 'سفارش شما با موفقیت ثبت شد'
 *     }
 *   }));
 *
 * این روش هیچ wiring اضافه‌ای نمی‌خواد — فقط `dispatchEvent` کافیه.
 *
 * علاوه بر این، اتفاقات داخلی زیر رو خودکار اعلان می‌کنه:
 *   - تغییر کاربر (login/logout) → از طریق رویداد 'userChanged' که App.js قبلاً ارسال می‌کنه
 *   - تغییر قابل‌توجه قیمت طلا (بیش از ۲٪) — اختیاری
 */

export const NotificationListener = () => {
  const { addNotification } = useNotifications();
  const lastUserRef = useRef(null);
  const lastGoldPriceRef = useRef(null);

  // ─── ۱) گوش‌کردن به رویداد custom 'notify' ─────────────────────
  useEffect(() => {
    const handler = (e) => {
      const detail = e?.detail || {};
      if (!detail.title) return;
      addNotification({
        type:    detail.type    || NOTIFICATION_TYPES.SYSTEM,
        title:   detail.title,
        message: detail.message || '',
        link:    detail.link    || null,
      });
    };
    window.addEventListener('notify', handler);
    return () => window.removeEventListener('notify', handler);
  }, [addNotification]);

  // ─── ۲) رویدادهای login و logout ─────────────────────────────
  useEffect(() => {
    const handleUserChange = () => {
      try {
        const raw = localStorage.getItem('user');
        const user = raw ? JSON.parse(raw) : null;
        const prevUser = lastUserRef.current;

        // ورود
        if (user && !prevUser) {
          addNotification({
            type:    NOTIFICATION_TYPES.SYSTEM,
            title:   'به جواهری نور خوش آمدید',
            message: `${user.first_name || user.phone || 'کاربر گرامی'}، حساب شما با موفقیت متصل شد.`,
          });
        }
        // خروج
        else if (!user && prevUser) {
          // اعلان خروج هم اضافه می‌کنیم، ولی به‌خاطر page reload
          // ممکنه نمایش داده نشه — مهم نیست، در state هست
        }
        lastUserRef.current = user;
      } catch (err) {
        console.warn('handleUserChange failed:', err);
      }
    };

    // بار اول
    handleUserChange();
    // وقتی کاربر تغییر کرد
    window.addEventListener('userChanged', handleUserChange);
    return () => window.removeEventListener('userChanged', handleUserChange);
  }, [addNotification]);

  // ─── ۳) پایش تغییرات بزرگ قیمت طلا ────────────────────────────
  // هر ۵ دقیقه قیمت طلا رو چک می‌کنه؛ اگه >2٪ تغییر کرد، اعلان می‌سازه
  useEffect(() => {
    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

    const checkPrice = async () => {
      try {
        const res = await fetch(`${API_BASE}/live-gold-prices/`, {
          signal: AbortSignal.timeout(8000)
        });
        if (!res.ok) return;
        const data = await res.json();
        const price = Number(data?.gold18?.price) || 0;
        if (!price) return;

        const last = lastGoldPriceRef.current;
        if (last && last > 0) {
          const change = ((price - last) / last) * 100;
          if (Math.abs(change) >= 2) {
            const direction = change > 0 ? 'افزایش' : 'کاهش';
            addNotification({
              type:    NOTIFICATION_TYPES.PRICE,
              title:   `${direction} قابل‌توجه قیمت طلا`,
              message: `قیمت طلای ۱۸ عیار ${Math.abs(change).toFixed(1)}٪ ${direction} یافت — اکنون ${price.toLocaleString()} تومان/گرم`,
            });
          }
        }
        lastGoldPriceRef.current = price;
      } catch {
        // silent — قیمت در دسترس نیست
      }
    };

    // اولین چک با ۳۰ ثانیه تأخیر (تا اپ load بشه)
    const initialTimer = setTimeout(checkPrice, 30 * 1000);
    // چک‌های بعدی هر ۵ دقیقه
    const interval = setInterval(checkPrice, 5 * 60 * 1000);
    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
    };
  }, [addNotification]);

  return null; // این کامپوننت چیزی render نمی‌کنه
};

// ─── ابزار کمکی برای فراخوانی از هر جای پروژه ──────────────────────
// import { notify } from './components/NotificationListener';
// notify({ type: 'order', title: '...', message: '...' });
export const notify = ({ type, title, message, link }) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('notify', {
    detail: { type, title, message, link }
  }));
};

export default NotificationListener;